import React, { useCallback } from 'react'
import { Card, Typography, Button, Stack, Tooltip, Avatar, useTheme } from '@mui/material'
import { useFormContext } from 'react-hook-form'
import { shallowEqual, useSelector } from 'react-redux'
import { selectRoutesMetaDataByDay } from 'slices/configuredService/configuredServiceScheduleSlice'
import { sumBy } from 'lodash'
import { WEEK_DAYS } from 'settings/constants/day'
import T from 'T'

const weekDays = [
  { day: WEEK_DAYS[0], color: '#F97316', t: T.SUNDAY },
  { day: WEEK_DAYS[1], color: '#E879F9', t: T.MONDAY },
  { day: WEEK_DAYS[2], color: '#A78BFA', t: T.TUESDAY },
  { day: WEEK_DAYS[3], color: '#34D399', t: T.WEDNESDAY },
  { day: WEEK_DAYS[4], color: '#FB7185', t: T.THURSDAY },
  { day: WEEK_DAYS[5], color: '#FBBF24', t: T.FRIDAY },
  { day: WEEK_DAYS[6], color: '#60A5FA', t: T.SATURDAY },
]

const ActiveServiceDaysSelector = () => {
  const { setValue, watch } = useFormContext()
  const theme = useTheme()
  const routesMetaDataByDay = useSelector(selectRoutesMetaDataByDay, shallowEqual)

  const getServiceCountForDay = useCallback(
    day => {
      const routes = routesMetaDataByDay[day] || []
      return Array.isArray(routes) ? sumBy(routes, ({ serviceCount }) => parseInt(serviceCount, 10)) : 0
    },
    [routesMetaDataByDay]
  )

  const handleClearSelection = useCallback(() => {
    weekDays.forEach(d => {
      setValue(`selectedDays.${d.day}`, false)
    })
  }, [setValue])

  const handleToggleDay = useCallback(
    day => {
      const currentVal = watch(`selectedDays.${day}`)
      setValue(`selectedDays.${day}`, !currentVal)
    },
    [watch, setValue]
  )

  return (
    <Card variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Typography variant="subtitle1" fontWeight="bold">
          {T.SERVICE_DAYS}
        </Typography>
        <Button size="small" onClick={handleClearSelection} sx={{ textTransform: 'none', padding: 0, minWidth: 'auto' }}>
          {T.CLEAR_SELECTION}
        </Button>
      </Stack>
      <Stack direction="row" spacing={1} justifyContent="space-between">
        {weekDays.map(({ day, color, t }) => {
          const isSelected = watch(`selectedDays.${day}`)
          const count = getServiceCountForDay(day)

          return (
            <Tooltip key={day} title={`${t}: ${count} ${T.SERVICES.toLowerCase()}`} arrow>
              <Avatar
                onClick={() => handleToggleDay(day)}
                sx={{
                  bgcolor: color,
                  width: 32,
                  height: 32,
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  opacity: isSelected ? 1 : 0.4,
                  border: isSelected ? `2.5px solid ${theme.palette.primary.main}` : '2.5px solid transparent',
                  boxSizing: 'border-box', // Ensure border doesn't expand size
                  transition: 'all 0.2s',
                  '&:hover': {
                    opacity: 1,
                  },
                }}
              >
                {t.charAt(0)}
              </Avatar>
            </Tooltip>
          )
        })}
      </Stack>
    </Card>
  )
}

export default ActiveServiceDaysSelector
